import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import AvailabilityToggle from './AvailabilityToggle';
import EligibilityReminderCard from '../community/EligibilityReminderCard';
import HealthTipsSection from '../community/HealthTipsSection';
import DonorFeedbackSection from '../community/DonorFeedbackSection';
import './DonorProfile.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const DonorProfilePage = () => {
    const { user, token, updateUser } = useAuth();
    const [profile, setProfile] = useState(null);
    const [loading, setLoading] = useState(true);
    const [editing, setEditing] = useState(false);
    const [saving, setSaving] = useState(false);
    const [editForm, setEditForm] = useState({ name: '', city: '', area: '', phone: '' });
    const [editError, setEditError] = useState('');

    useEffect(() => {
        const fetchProfile = async () => {
            try {
                const res = await axios.get(`${API_URL}/donors/${user._id}`, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                setProfile(res.data);
                setEditForm({
                    name: res.data.name || '',
                    city: res.data.city || '',
                    area: res.data.area || '',
                    phone: res.data.phone || ''
                });
            } catch (err) {
                console.error('Failed to load profile:', err);
            } finally {
                setLoading(false);
            }
        };
        if (user) fetchProfile();
    }, [user, token]);

    const handleEditSave = async () => {
        if (editing) {
            // Save mode
            setSaving(true);
            setEditError('');
            try {
                const res = await axios.put(`${API_URL}/donors/${user._id}`, editForm, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                setProfile(res.data);
                // Bug Fix BUG-NEW-H1: sync updated fields to AuthContext/localStorage
                updateUser({ name: res.data.name, city: res.data.city, area: res.data.area, phone: res.data.phone });
                setEditing(false);
            } catch (err) {
                setEditError(err.response?.data?.message || 'Failed to update profile');
            } finally {
                setSaving(false);
            }
        } else {
            // Enter edit mode
            setEditing(true);
        }
    };

    const handleCancel = () => {
        setEditing(false);
        setEditError('');
        setEditForm({
            name: profile.name || '',
            city: profile.city || '',
            area: profile.area || '',
            phone: profile.phone || ''
        });
    };

    if (loading) return <div className="loading-screen">Loading profile...</div>;
    if (!profile) return <div className="error-screen">Failed to load profile</div>;

    return (
        <div className="profile-page">
            <div className="profile-card">
                <div className="profile-header">
                    <div className="profile-avatar">
                        {profile.name?.charAt(0).toUpperCase()}
                    </div>
                    <div className="profile-info">
                        <h1 className="profile-name">
                            {profile.name}
                            {profile.isVerified && <span className="verified-badge" title="Verified Donor">✓</span>}
                        </h1>
                        <p className="profile-email">{profile.email}</p>
                    </div>
                    <div className="blood-type-big">{profile.bloodType}</div>
                </div>

                <div className="stats-row">
                    <div className="stat-card">
                        <span className="stat-value">{profile.donationCount}</span>
                        <span className="stat-label">Donations</span>
                    </div>
                    <div className="stat-card" title="Each blood donation can save up to 3 lives (Red Cross estimate)">
                        <span className="stat-value">{profile.donationCount * 3}</span>
                        <span className="stat-label">Lives Impacted ℹ️</span>
                    </div>
                    <div className="stat-card">
                        <span className="stat-value">
                            {profile.nextEligibleDate
                                ? new Date(profile.nextEligibleDate).toLocaleDateString()
                                : 'Eligible Now'}
                        </span>
                        <span className="stat-label">Next Eligible</span>
                    </div>
                </div>

                <div className="availability-section">
                    <h3>Availability Status</h3>
                    <div className={`availability-indicator ${profile.isAvailable ? 'available' : 'unavailable'}`}>
                        {profile.isAvailable ? '🟢 Available to Donate' : '🔴 Not Available'}
                    </div>
                    <AvailabilityToggle
                        donorId={user._id}
                        token={token}
                        isAvailable={profile.isAvailable}
                        nextEligibleDate={profile.nextEligibleDate}
                        onUpdate={(newAvail, newDate) => {
                            setProfile(prev => ({
                                ...prev,
                                isAvailable: newAvail,
                                nextEligibleDate: newDate
                            }));
                        }}
                    />
                </div>

                {profile.badges && profile.badges.length > 0 && (
                    <div className="badges-section">
                        <h3>Badges</h3>
                        <div className="badges-grid">
                            {profile.badges.map((badge, i) => (
                                <span key={i} className="badge">{badge}</span>
                            ))}
                        </div>
                    </div>
                )}

                {/* Anika — F14: Eligibility Reminder & Health Tips */}
                <EligibilityReminderCard
                    nextEligibleDate={profile.nextEligibleDate}
                    lastDonationDate={profile.lastDonationDate}
                />
                <HealthTipsSection />

                <div className="profile-details">
                    <div className="profile-details-header">
                        <h3>Details</h3>
                        <div className="profile-edit-buttons">
                            {editing && (
                                <button className="btn-cancel-edit" onClick={handleCancel}>Cancel</button>
                            )}
                            <button
                                className={`btn-edit-profile ${editing ? 'btn-save' : ''}`}
                                onClick={handleEditSave}
                                disabled={saving}
                            >
                                {saving ? 'Saving...' : editing ? '✓ Save' : '✎ Edit Profile'}
                            </button>
                        </div>
                    </div>
                    {editError && <div className="edit-error">{editError}</div>}

                    <div className="detail-row">
                        <span>Name:</span>
                        {editing ? (
                            <input
                                type="text"
                                className="edit-input"
                                value={editForm.name}
                                onChange={(e) => setEditForm(prev => ({ ...prev, name: e.target.value }))}
                            />
                        ) : (
                            <span>{profile.name || 'Not set'}</span>
                        )}
                    </div>
                    <div className="detail-row">
                        <span>City:</span>
                        {editing ? (
                            <input
                                type="text"
                                className="edit-input"
                                value={editForm.city}
                                onChange={(e) => setEditForm(prev => ({ ...prev, city: e.target.value }))}
                            />
                        ) : (
                            <span>{profile.city || 'Not set'}</span>
                        )}
                    </div>
                    <div className="detail-row">
                        <span>Area:</span>
                        {editing ? (
                            <input
                                type="text"
                                className="edit-input"
                                value={editForm.area}
                                onChange={(e) => setEditForm(prev => ({ ...prev, area: e.target.value }))}
                            />
                        ) : (
                            <span>{profile.area || 'Not set'}</span>
                        )}
                    </div>
                    <div className="detail-row">
                        <span>Phone:</span>
                        {editing ? (
                            <input
                                type="text"
                                className="edit-input"
                                value={editForm.phone}
                                onChange={(e) => setEditForm(prev => ({ ...prev, phone: e.target.value }))}
                            />
                        ) : (
                            <span>{profile.phone || 'Not set'}</span>
                        )}
                    </div>
                    <div className="detail-row">
                        <span>Verification:</span>
                        <span className={`status-${profile.verificationStatus}`}>
                            {profile.verificationStatus?.charAt(0).toUpperCase() + profile.verificationStatus?.slice(1)}
                            {profile.verificationStatus === 'none' && (
                                <Link to="/verification" className="verify-link"> — Get Verified →</Link>
                            )}
                            {profile.verificationStatus === 'rejected' && (
                                <Link to="/verification" className="verify-link"> — Try Again →</Link>
                            )}
                        </span>
                    </div>
                </div>

                {/* Anika — F15: Public Feedback from Requesters */}
                <DonorFeedbackSection donorId={profile._id} />
            </div>
        </div>
    );
};

export default DonorProfilePage;

